import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  InputAdornment,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Tooltip,
  FormLabel,
} from '@mui/material'

import {
  AccountCircle,
  Public,
  Facebook,
  Instagram,
  LinkedIn,
  Twitter,
  YouTube,
  OtherHouses,
  Info,
} from '@mui/icons-material'
import { hasAnswerChanged, isValidHttpUrl } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import QuestionButton from '../QuestionButton'

export default function QuestionOne(props) {
  const { questionData, saveResponses } = props
  const classes = styles()

  const initialState = {
    site: '',
    facebook: '',
    instagram: '',
    linkedin: '',
    twitter: '',
    youtube: '',
    tiktok: '',
    other: '',
  }

  const initialErrors = {
    site: false,
    facebook: false,
    instagram: false,
    linkedin: false,
    twitter: false,
    youtube: false,
    tiktok: false,
    other: false,
  }

  const convertArrayToFormFormat = (values) => {
    const initialValues = cloneDeep(initialState)
    values.forEach((element) => {
      initialValues[element.type] = element.address
    })

    return initialValues
  }

  const initializeFormState = (data) => {
    return {
      hasProfessionalSocialMedia: data.items?.length > 0,
      items: data.items?.length > 0 ? data.items : [],
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [socialMedia, setSocialMedia] = useState(
    questionData.has_answer
      ? convertArrayToFormFormat(questionData.items)
      : initialState,
  )

  const [socialMediaErrors, setSocialMediaErrors] = useState(initialErrors)

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const validateLinks = (list) => {
    return list.map((link) => {
      return {
        type: link.type,
        is_invalid: !isValidHttpUrl(link.address),
      }
    })
  }

  const setSocialMediaArray = (socialMediaObj) => {
    const types = Object.keys(socialMediaObj).filter(
      (key) => socialMediaObj[key] !== '',
    )
    const socialMediaList = types.map((type) => {
      return { type: type, address: socialMediaObj[type] }
    })

    const newFormState = cloneDeep(formState)
    newFormState.items = socialMediaList
    setFormState(newFormState)
  }

  const handleFieldChange = (event) => {
    const key = event.target.id
    const newSocialMedia = cloneDeep(socialMedia)
    newSocialMedia[key] = event.target.value
    setSocialMedia(newSocialMedia)
    setSocialMediaArray(newSocialMedia)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasProfessionalSocialMedia = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const isResponseValid = () => {
    let validResponse = true
    if (formState.hasProfessionalSocialMedia) {
      validResponse = formState.items.length > 0
    }

    const errors = validateLinks(formState.items)

    if (errors.length > 0 && errors.some((error) => error.is_invalid)) {
      toast.error(
        'Links devem conter ao menos o protocolo e domínio. Exemplo: "https://exemplo.com/"',
      )
      const linkErrors = cloneDeep(initialErrors)
      errors.forEach((error) => {
        linkErrors[error.type] = error.is_invalid
      })

      setSocialMediaErrors(linkErrors)

      return false
    }

    return validResponse
  }

  const validateAndSave = async () => {
    const newQuestionData = cloneDeep(questionData)
    if (isResponseValid()) {
      setLoading(true)
      newQuestionData.has_answer = true

      newQuestionData.items = formState.items
      await saveResponses(
        newQuestionData,
        'social_networks',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(() => {
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    } else if (formState.items.length === 0) {
      toast.error(
        'Ao selecionar "Sim" deve-se preencher ao menos um dos campos',
      )
    }
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasProfessionalSocialMedia !==
        questionData.items.length > 0
    setEdited(hasChanged)
    if (
      formState.items.length > 0 &&
      formState.hasProfessionalSocialMedia === false
    ) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setFormState(initializeFormState(questionData))
    setSaved(questionData.has_answer)
    setSocialMedia(convertArrayToFormFormat(questionData.items))
  }, [questionData])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 1</h3>
        <div className={classes.form}>
          <FormLabel>
            Possui site e/ou redes sociais para fins profissionais?{' '}
            <Tooltip
              title="Aqui é necessário informar caso utilize suas
                redes sociais e/ou site para realizar qualquer divulgação de 
                eventuais atividades profissionais realizadas, envolvendo a 
                Assessoria de Investimentos ou não."
            >
              <Info fontSize="small" color="info" />
            </Tooltip>
          </FormLabel>

          <FormControl>
            <RadioGroup
              row
              value={formState.hasProfessionalSocialMedia ? 'yes' : 'no'}
              name="hasSocialMedia"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasProfessionalSocialMedia ? (
            <div className={classes.formFields}>
              <p>Informe, caso haja, os seguintes endereços eletrônicos:</p>
              <TextField
                id="site"
                label="Site"
                onChange={handleFieldChange}
                value={socialMedia.site}
                error={socialMediaErrors.site}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Public />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="facebook"
                label="Facebook"
                onChange={handleFieldChange}
                value={socialMedia.facebook}
                error={socialMediaErrors.facebook}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Facebook />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="instagram"
                label="Instagram"
                onChange={handleFieldChange}
                value={socialMedia.instagram}
                error={socialMediaErrors.instagram}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Instagram />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="linkedin"
                label="LinkedIn"
                onChange={handleFieldChange}
                value={socialMedia.linkedin}
                error={socialMediaErrors.linkedin}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkedIn />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="twitter"
                label="Twitter"
                onChange={handleFieldChange}
                value={socialMedia.twitter}
                error={socialMediaErrors.twitter}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Twitter />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="youtube"
                label="YouTube"
                onChange={handleFieldChange}
                value={socialMedia.youtube}
                error={socialMediaErrors.youtube}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <YouTube />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="tiktok"
                label="TikTok"
                onChange={handleFieldChange}
                value={socialMedia.tiktok}
                error={socialMediaErrors.tiktok}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="other"
                label="Outros (separado por vírgula)"
                onChange={handleFieldChange}
                value={socialMedia.other}
                error={socialMediaErrors.other}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <OtherHouses />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasProfessionalSocialMedia &&
              formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
